import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell } from 'lucide-react'
import { Sheet } from './UI'
import { ReelRating } from './ReelRating'
import { ClapboardClosed } from './ClapboardIcon'
import { rateEpisode } from '../hooks/useEpisodeRatings'
import { useAuth } from '../hooks/useAuth'
import { subscribeReceipt, currentReceipt, endSession } from '../lib/batchReceipt'
import { claimRail, releaseRail } from '../lib/toastRail'
import { isOverlayBusy } from '../lib/overlayBusy'
import { logEvent } from '../lib/analytics'
import { requestPermission, subscribeToPush } from '../lib/push'
import { safeStorage } from '../lib/safeStorage'
import { PUSH_DISMISS_KEY } from './NotificationNudge'

const EYE = { fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--amber)' }
const RAIL_ID = 'receipt'

// ── BatchReceipt — the one sheet a cascade mark gets ─────────────────────────
// See lib/batchReceipt.js for the collector. This renders whatever it held:
// Undo at the top with no clock on it, the rate rows for every episode in the
// run, then "What that earned" as plain facts (a season wrapped, a streak kept,
// an achievement, a new card) with a door on each instead of a timer. One Done.
//
// The cinematics stay themselves: a wrap plays first (we wait behind it), and a
// new card's reveal plays from its row's "See it". Everything else that used to be
// a timed toast or a full-screen medallion is a row here.
export function BatchReceipt() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [session, setSession] = useState(() => currentReceipt())
  const [ready, setReady] = useState(false)
  const [ratings, setRatings] = useState({})
  const [pushState, setPushState] = useState(null)   // null | 'busy' | 'on' | 'no'

  useEffect(() => subscribeReceipt(s => setSession(s ? { ...s, items: [...s.items] } : null)), [])

  const showing = !!session?.showing
  // Wait behind the wrap cinematic (or anything else that owns the screen).
  useEffect(() => {
    if (!showing) { setReady(false); return }
    const tick = () => { if (!isOverlayBusy()) setReady(true) }
    tick()
    const iv = setInterval(tick, 300)
    return () => clearInterval(iv)
  }, [showing])

  const open = showing && ready
  useEffect(() => {
    if (!open) return
    const prevHold = window.__nextepModalHold
    window.__nextepModalHold = true
    claimRail(RAIL_ID)
    setRatings({}); setPushState(null)
    logEvent('batch_receipt_shown', { n: session.mark?.count, items: session.items.map(i => i.kind) })
    return () => { window.__nextepModalHold = prevHold; releaseRail(RAIL_ID) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  if (!open) return null

  const mark = session.mark || {}
  const items = session.items
  const rate = items.find(i => i.kind === 'rate')
  const eps = rate?.detail?.episodes || []
  const facts = items.filter(i => i.kind !== 'undo' && i.kind !== 'rate')
  const keepsake = items.find(i => i.kind === 'keepsake')
  const askPush = !!user && !user.is_anonymous && typeof Notification !== 'undefined'
    && Notification.permission === 'default' && safeStorage.get(PUSH_DISMISS_KEY) !== '1'

  const close = () => {
    // The receipt WAS the push ask, whether or not they answered it — the nudge
    // asks once, and it must not rise a second time right after this closes.
    if (askPush) { try { safeStorage.set(PUSH_DISMISS_KEY, '1') } catch {} }
    endSession()
  }
  const undo = () => {
    try { mark.undo?.() } catch { /* never break on undo */ }
    logEvent('batch_receipt_undo', { n: mark.count })
    endSession()
  }
  const rateOne = (season, episode, v) => {
    setRatings(r => ({ ...r, [`${season}-${episode}`]: v }))
    rateEpisode(mark.showId, user?.id, season, episode, v)
  }
  const seeCard = () => {
    close()
    // Hand the card back to its reveal, marked so the collector lets it through.
    try { window.dispatchEvent(new CustomEvent('nextep:keepsake-minted', { detail: { ...keepsake.detail, _replay: true } })) } catch {}
  }
  const enablePush = async () => {
    setPushState('busy')
    let res = 'denied'
    try { res = await requestPermission(); if (res === 'granted') await subscribeToPush(user.id) } catch { /* noop */ }
    logEvent('push_nudge_answered', { granted: res === 'granted', via: 'receipt' })
    setPushState(res === 'granted' ? 'on' : 'no')
  }

  const ep = mark.watchedEp || {}
  const from = eps.length ? Math.min(...eps.map(e => e.episode)) : null
  const to = eps.length ? Math.max(...eps.map(e => e.episode)) : ep.episode
  const range = from != null && from !== to ? `S${ep.season}·E${from}–E${to}` : (ep.season ? `S${ep.season}·E${ep.episode}` : '')

  return (
    <Sheet open onClose={close} label="Marked" from="bottom" panelStyle={{ padding: '10px 20px calc(24px + env(safe-area-inset-bottom))', background: 'var(--bg-raised)', maxHeight: '86dvh', overflowY: 'auto' }}>
      <div style={{ width: 36, height: 4, borderRadius: 999, background: 'var(--border)', margin: '0 auto 16px' }} />
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ ...EYE, display: 'flex', alignItems: 'center', gap: 6 }}><ClapboardClosed size={12} color="var(--amber)" />Marked · {range}</div>
          <div style={{ fontFamily: 'var(--font-serif)', fontSize: 24, fontWeight: 500, color: 'var(--text-strong)', lineHeight: 1.15, marginTop: 4 }}>{mark.count} episodes, marked.</div>
          {mark.showName && <div style={{ fontSize: 13.5, color: 'var(--text-muted)', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{mark.showName}</div>}
        </div>
        <button onClick={undo} className="ui-hit" style={{ flexShrink: 0, height: 34, padding: '0 14px', borderRadius: 'var(--radius-pill)', border: '1px solid rgba(var(--amber-rgb),0.3)', background: 'rgba(var(--amber-rgb),0.12)', color: 'var(--amber)', fontFamily: 'var(--font)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Undo</button>
      </div>

      {eps.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <div style={{ ...EYE, marginBottom: 4 }}>Rate them</div>
          <div style={{ fontSize: 12.5, color: 'var(--text-dim)', marginBottom: 4 }}>As many as you like. The rest stay unrated.</div>
          {eps.map(({ season, episode }) => {
            const key = `${season}-${episode}`
            return (
              <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '9px 0', borderBottom: '1px solid var(--border-soft)' }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-strong)', fontVariantNumeric: 'tabular-nums' }}>S{season}·E{episode}</span>
                <ReelRating value={ratings[key] || 0} onChange={v => rateOne(season, episode, v)} size={20} showLabel={false} />
              </div>
            )
          })}
        </div>
      )}

      {(facts.length > 0 || askPush) && (
        <div style={{ marginTop: 20 }}>
          <div style={{ ...EYE, marginBottom: 6 }}>What that earned</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {facts.map(f => <FactRow key={f.key} item={f} navigate={navigate} close={close} seeCard={seeCard} />)}
            {askPush && (
              <Row icon={<Bell size={16} />} title="Episode alerts" body={pushState === 'on' ? 'On. One heads-up the day the next one airs.' : pushState === 'no' ? 'Not this time.' : 'A heads-up the day the next one airs.'}
                action={!pushState ? { label: 'Turn on', onClick: enablePush } : pushState === 'busy' ? { label: '…' } : null} />
            )}
          </div>
        </div>
      )}

      <button onClick={close} className="ui-hit" style={{ marginTop: 22, width: '100%', height: 46, borderRadius: 'var(--radius-pill)', border: 'none', background: 'var(--amber)', color: 'var(--amber-ink)', fontFamily: 'var(--font)', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>Done</button>
    </Sheet>
  )
}

function FactRow({ item, navigate, close, seeCard }) {
  const d = item.detail || {}
  const go = (route) => () => { close(); if (route) navigate(route) }
  switch (item.kind) {
    case 'wrap':
      return <Row icon="🎬" title={d.type === 'series' ? 'Series finished' : d.type === 'caughtup' ? `Caught up on Season ${d.season || ''}` : `Season ${d.season} wrapped`} body={d.showName} />
    case 'achievement':
      return <Row icon={d.icon || '🏆'} title={d.label} body={d.desc} eyebrow="Achievement" action={{ label: 'See', onClick: go('/profile?tab=achievements') }} />
    case 'streak':
      return <Row icon={d.icon || '🔥'} title={d.title} body={d.body} eyebrow="Streak" action={{ label: 'See', onClick: go(d.route || '/profile?tab=stats') }} />
    case 'milestone': {
      // The streak toast's title carries its own flame; the tile already shows it.
      const title = d.icon && d.title?.startsWith(d.icon) ? d.title.slice(d.icon.length).trim() : d.title
      return <Row icon={d.icon || '✨'} title={title} body={d.body} action={d.route ? { label: 'See', onClick: go(d.route) } : null} />
    }
    case 'keepsake':
      return <Row icon="🃏" title={`Card No. ${d.no || ''}`.trim()} body={`${d.show_name || ''} · yours to keep`} eyebrow="Final Cut" action={{ label: 'See it', onClick: seeCard }} />
    default:
      return null
  }
}

function Row({ icon, eyebrow, title, body, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
      <span style={{ flexShrink: 0, width: 36, height: 36, borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, background: 'rgba(var(--amber-rgb),0.1)', border: '1px solid rgba(var(--amber-rgb),0.25)', color: 'var(--amber)' }}>{icon}</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        {eyebrow && <span style={{ ...EYE, fontSize: 9.5, display: 'block', marginBottom: 1 }}>{eyebrow}</span>}
        <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: 'var(--text-strong)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</span>
        {body && <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-muted)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{body}</span>}
      </span>
      {action && (
        <button onClick={action.onClick} className="ui-hit" style={{ flexShrink: 0, height: 30, padding: '0 12px', borderRadius: 'var(--radius-pill)', border: '1px solid var(--border)', background: 'var(--bg-raised)', color: 'var(--text)', fontFamily: 'var(--font)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>{action.label}</button>
      )}
    </div>
  )
}
